import React, { useEffect, useRef, useState } from 'react';
import './YouTubePlayer.css';

let ytApiPromise = null;
const loadYouTubeAPI = () => {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const prevReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prevReady === 'function') prevReady();
      resolve(window.YT);
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiPromise;
};

const MuteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);
const VolumeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const extractId = (val) => {
  if (!val) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(val)) return val;
  const m = val.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const YouTubePlayer = ({ videoIds }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [ready, setReady] = useState(false);
  // Hide the iframe completely behind a black cover until the video has been
  // playing for ~600ms. That window is long enough for YouTube's title bar
  // and centre play overlay to vanish, so the user only sees the raw video.
  const [revealed, setRevealed] = useState(false);

  // Normalize input to a clean array of 11-char video IDs.
  const ids = (Array.isArray(videoIds) ? videoIds : [videoIds])
    .map(extractId)
    .filter(Boolean);

  useEffect(() => {
    if (!ids.length || !containerRef.current) return;
    let cancelled = false;
    let player = null;
    const target = containerRef.current;

    loadYouTubeAPI().then((YT) => {
      if (cancelled) return;
      const firstId = ids[0];
      player = new YT.Player(target, {
        videoId: firstId,
        host: 'https://www.youtube-nocookie.com',
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 1,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0,
          playsinline: 1,
          mute: 0,
          loop: 1,
          playlist: ids.length === 1 ? firstId : ids.join(','),
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            playerRef.current = e.target;
            setReady(true);
            // Try to play with sound. If the browser blocks autoplay-with-sound
            // (Chrome / Safari / Firefox all do unless the site has engagement),
            // fall back to muted autoplay so the video at least starts.
            try {
              e.target.unMute();
              e.target.playVideo();
            } catch (_) {}
            setTimeout(() => {
              if (cancelled || !e.target) return;
              try {
                const isMuted = e.target.isMuted();
                const state = e.target.getPlayerState();
                setMuted(isMuted);
                if (state !== YT.PlayerState.PLAYING) {
                  // Browser likely blocked unmuted autoplay — try muted.
                  e.target.mute();
                  e.target.playVideo();
                  setMuted(true);
                }
              } catch (_) {}
            }, 350);
          },
          onStateChange: (e) => {
            if (cancelled) return;
            const s = e.data;
            if (s === YT.PlayerState.PLAYING) {
              // Wait for YouTube's title overlay + centre play splash to
              // finish their fade before lifting our cover.
              setTimeout(() => {
                if (!cancelled) setRevealed(true);
              }, 600);
            }
          },
        },
      });
    });

    return () => {
      cancelled = true;
      try {
        if (player && player.destroy) player.destroy();
      } catch (_) {}
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  const toggleMute = () => {
    if (!ready || !playerRef.current) return;
    if (muted) {
      playerRef.current.unMute();
      setMuted(false);
    } else {
      playerRef.current.mute();
      setMuted(true);
    }
  };

  if (!ids.length) return null;

  return (
    <div className="yt-player">
      <div className="yt-frame">
        {/* The inner div is what YT.Player replaces with an iframe. */}
        <div className="yt-iframe-host">
          <div ref={containerRef} className="yt-iframe-target" />
        </div>
        {/* Transparent shield captures clicks/hover so the YouTube title and
            "Watch on YouTube" overlays cannot be interacted with. */}
        <div className="yt-shield" />
        {/* Opaque cover that hides every YouTube overlay (title bar, centre
            play splash, suggested videos preview) until the video has been
            playing for a moment. */}
        <div className={"yt-cover" + (revealed ? ' is-revealed' : '')} aria-hidden="true" />
        <div className="yt-controls">
          <button
            type="button"
            className="yt-btn"
            onClick={toggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            disabled={!ready}
          >
            {muted ? <MuteIcon /> : <VolumeIcon />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;
